import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useZodForm } from '@/lib/forms/useZodForm'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import {
  ArchiveX,
  CalendarIcon,
  Globe,
  Settings as SettingsIcon,
  ArchiveRestore,
  Scale,
  Route as RouteIcon,
  Laptop,
  Shuffle,
  Users,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import TimeZoneSelect from '@/components/timezoneSelect'
import LoadingSpinner from '@/components/LoadingSpinner'
import { FaRegCalendar } from 'react-icons/fa6'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils/ui'
import { format } from 'date-fns'
import { AppCalendar } from '@/components/AppCalendar'
import { Input } from '@/components/ui/input'
import { PhoneNumberInput } from '@/components/ui/phone-number-input'
import { FaRegClock } from 'react-icons/fa'
import { useEffect, useMemo, useRef, useState } from 'react'
import { isPossiblePhoneNumber, type Country } from 'react-phone-number-input'
import { Switch } from '@/components/ui/switch'
import { useFeatures } from '@/lib/hooks/useFeatures'
import { useRoleKey } from '@/lib/states/session/hooks'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import AlertBlocker from '@/components/AlertBlocker'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { patchKeyedListCache, patchListCache } from '@trainrr/api-client'

const clientGenderOptions = [
  'male',
  'female',
  'non-binary',
  'other',
  'prefer not to say',
] as const

type ClientGenderOption = (typeof clientGenderOptions)[number]

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/settings',
)({
  component: Settings,
  validateSearch: (search: Record<string, unknown>) => ({
    section: search.section as string | undefined,
  }),
  loader: async ({ params: { clientId } }) => {
    await Promise.all([
      trpcUtils.features.listForClient.ensureData({ clientId }),
      trpcUtils.features.listCatalog.ensureData(),
    ])
  },
  pendingComponent: () => <ClientSettingsLoadingState />,
})

const ClientSettingsLoadingState = () => (
  <div className="w-full md:w-[90%] mx-auto">
    <Card className="p-6 space-y-8">
      <div className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-10 w-[240px]" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-10 w-[240px]" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-64" />
        <div className="flex gap-3">
          <Skeleton className="h-10 w-[180px]" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-10 w-full max-w-[320px]" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-72" />
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
      <Skeleton className="h-px w-full" />
      <Skeleton className="h-10 w-32" />
    </Card>
  </div>
)

function Settings() {
  const search = Route.useSearch()
  const { clientId } = Route.useParams()

  const featuresRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [updatingFeatureKey, setUpdatingFeatureKey] = useState<string | null>(
    null,
  )
  const [archiveBlockedOpen, setArchiveBlockedOpen] = useState(false)
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false)
  const [defaultCountry, setDefaultCountry] = useState<Country>('US')
  const currentUserRoleKey = useRoleKey()

  const utils = trpc.useUtils()
  const setClientOverride = trpc.features.setClientOverride.useMutation()
  const { data: client, isLoading: clientLoading } = trpc.clients.get.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )
  const updateSettings = trpc.clients.updateSettings.useMutation()
  const archiveClientMutation = trpc.clients.archive.useMutation()
  const unarchiveClientMutation = trpc.clients.unarchive.useMutation()
  const {
    data: clientFeatures,
    isLoading: clientFeaturesLoading,
  } = trpc.features.listForClient.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )
  const { data: features, isLoading: featuresLoading } = useFeatures()
  const {
    data: activeSubscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = trpc.packages.hasActiveSubscription.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )

  const canArchiveClient =
    client?.status !== 'ARCHIVED' &&
    (currentUserRoleKey === 'owner' ||
      currentUserRoleKey === 'admin' ||
      Boolean(client?.assigned_to_current_user))

  const canUnarchiveClient =
    client?.status === 'ARCHIVED' &&
    (currentUserRoleKey === 'owner' || currentUserRoleKey === 'admin')

  if (subscriptionError) {
    console.error('Error fetching client subscriptions', subscriptionError)
  }

  // Auto-scroll to features section if requested
  useEffect(() => {
    if (search.section === 'features' && featuresRef.current) {
      featuresRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [search.section])

  const clientSettings = z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    phone: z
      .string()
      .trim()
      .refine(
        (value) => value === '' || isPossiblePhoneNumber(value),
        'Please enter a valid phone number',
      ),
    dob: z.date().optional(),
    gender: z.enum(clientGenderOptions).nullable().optional(),
    timezone: z.string().optional(),
    date: z.date().optional(),
    duration: z.coerce.number().optional(),
    check_in_day: z.coerce.number().min(0).max(6).optional(),
    weight_unit: z.enum(['kg', 'lbs']),
    distance_unit: z.enum(['km', 'mi']),
    client_category: z.enum(['online', 'in-person', 'hybrid']),
  })

  type ClientSettingsSchema = z.infer<typeof clientSettings>

  const resolvedClientCategory =
    client?.client_category === 'in-person' ||
    client?.client_category === 'hybrid'
      ? client.client_category
      : 'online'

  const defaultValues: Partial<ClientSettingsSchema> = useMemo(
    () => ({
      firstName: client?.first_name ?? '',
      lastName: client?.last_name ?? '',
      phone: client?.phone ?? '',
      dob: client?.dob ? new Date(client.dob) : undefined,
      gender: clientGenderOptions.includes(client?.gender as ClientGenderOption)
        ? (client?.gender as ClientGenderOption)
        : null,
      timezone: client?.timezone,
      date: client?.start_date ? new Date(client.start_date) : undefined,
      duration: client?.duration ?? undefined,
      check_in_day: client?.check_in_day ?? undefined,
      weight_unit: client?.weight_unit === 'lbs' ? 'lbs' : 'kg',
      distance_unit: client?.distance_unit === 'mi' ? 'mi' : 'km',
      client_category: resolvedClientCategory,
    }),
    [client, resolvedClientCategory],
  )

  const form = useZodForm(clientSettings, {
    defaultValues,
  })

  const isDirty = form.formState.isDirty

  useEffect(() => {
    if (!client) return
    if (form.formState.isDirty) return
    form.reset(defaultValues)
  }, [client?.id, defaultValues, form])

  useEffect(() => {
    const inferred = inferCountryFromLocale()
    if (inferred) {
      setDefaultCountry(inferred)
    }
  }, [])

  const patchClientCache = (
    updatedFields: Partial<NonNullable<typeof client>>,
  ) => {
    if (!client) return
    const updated = { ...client, ...updatedFields }
    utils.clients.get.setData({ clientId }, () => updated)
    patchListCache(utils.clients.list, (old) =>
      old.map((row) =>
        row.id === updated.id
          ? {
              ...row,
              first_name: updated.first_name,
              last_name: updated.last_name,
              phone: updated.phone,
              dob: updated.dob,
              gender: updated.gender,
              timezone: updated.timezone,
              start_date: updated.start_date,
              duration: updated.duration,
              check_in_day: updated.check_in_day,
              weight_unit: updated.weight_unit,
              distance_unit: updated.distance_unit,
              client_category: updated.client_category,
              status: updated.status,
              archived_at: updated.archived_at,
            }
          : row,
      ),
    )
  }

  if (clientLoading && !client) {
    return <ClientSettingsLoadingState />
  }

  async function onSubmit(data: ClientSettingsSchema) {
    if (!client) {
      toast.error('Client not found')
      return
    }
    setLoading(true)
    try {
      const updated = await updateSettings.mutateAsync({
        clientId: client.id,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phone: data.phone,
        dob: data.dob?.toISOString() ?? null,
        gender: data.gender ?? null,
        timezone: data.timezone,
        startDate: data.date?.toISOString() ?? null,
        duration: data.duration ?? null,
        checkInDay: data.check_in_day ?? null,
        weightUnit: data.weight_unit,
        distanceUnit: data.distance_unit,
        clientCategory: data.client_category,
      })
      patchClientCache(updated)
      form.reset({
        firstName: updated.first_name,
        lastName: updated.last_name,
        phone: data.phone ?? '',
        dob: data.dob,
        gender: data.gender,
        timezone: data.timezone,
        date: data.date,
        duration: data.duration,
        check_in_day: data.check_in_day,
        weight_unit: data.weight_unit,
        distance_unit: data.distance_unit,
        client_category: data.client_category,
      })
      toast.success('Client settings updated')
    } catch (error) {
      console.error('Error updating client settings', error)
      toast.error('There were errors updating some data')
    } finally {
      setLoading(false)
    }
  }

  function onInvalidSubmit() {
    toast.error('Please fix the highlighted errors before saving')
  }

  const archiveClient = async () => {
    if (!client) {
      toast.error('Client not found')
      return
    }
    if (subscriptionLoading) {
      toast.message('Checking subscription status...')
      return
    }
    if (activeSubscription) {
      setArchiveBlockedOpen(true)
      return
    }
    setLoading(true)
    try {
      const updated = await archiveClientMutation.mutateAsync({
        clientId: client.id,
      })
      patchClientCache({
        id: client.id,
        status: updated.status,
        archived_at: updated.archived_at,
      })
      void utils.billing.entitlements.invalidate()
      toast.success('Client archived successfully')
    } catch (error) {
      console.error('Error archiving client', error)
      toast.error('Failed to archive client')
    } finally {
      setLoading(false)
    }
  }

  const unarchiveClient = async () => {
    if (!client) {
      toast.error('Client not found')
      return
    }

    setLoading(true)
    try {
      const updated = await unarchiveClientMutation.mutateAsync({
        clientId: client.id,
      })
      patchClientCache({
        id: client.id,
        status: updated.status,
        archived_at: updated.archived_at,
      })
      void utils.billing.entitlements.invalidate()
      toast.success('Client unarchived successfully')
    } catch (error) {
      console.error('Error unarchiving client', error)
      toast.error('Failed to unarchive client')
    } finally {
      setLoading(false)
    }
  }

  const toggleFeature = async (featureKey: string, enabled: boolean) => {
    if (!client) {
      toast.error('Client not found')
      return
    }

    setUpdatingFeatureKey(featureKey)

    try {
      const updated = await setClientOverride.mutateAsync({
        clientId: client.id,
        featureKey,
        enabled,
      })
      patchKeyedListCache(
        utils.features.listForClient,
        { clientId: client.id },
        (old) =>
          old.map((row) =>
            row.feature_key === updated.feature_key
              ? { ...row, enabled: updated.enabled }
              : row,
          ),
      )
      const isEnabledInput = {
        clientId: client.id,
        featureKey,
      }
      const cachedEnabled = utils.features.isEnabled.getData(isEnabledInput)
      if (cachedEnabled == null) {
        void utils.features.isEnabled.invalidate(isEnabledInput)
      } else {
        utils.features.isEnabled.setData(isEnabledInput, () => updated.enabled)
      }
      toast.success(`Feature ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      console.error('Error updating feature', error)
      toast.error('Failed to update feature')
    } finally {
      setUpdatingFeatureKey(null)
    }
  }

  const isFeatureEnabled = (featureKey: string) => {
    if (!clientFeatures) return false
    const feature = clientFeatures.find((cf) => cf.feature_key === featureKey)
    return feature ? feature.enabled : false
  }

  return (
    <div className="w-full md:w-[90%] mx-auto">
      <AlertBlocker shouldBlock={isDirty && !loading} />
      <AlertDialog
        open={archiveConfirmOpen}
        onOpenChange={setArchiveConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove client access to the app, but keep their data for
              your records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setArchiveConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setArchiveConfirmOpen(false)
                void archiveClient()
              }}
              variant={'destructive'}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={archiveBlockedOpen}
        onOpenChange={setArchiveBlockedOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Active subscription</AlertDialogTitle>
            <AlertDialogDescription>
              You can&apos;t archive a client with an active subscription.
              Cancel their subscription first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setArchiveBlockedOpen(false)}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card className="p-6 mb-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
            className="space-y-8"
          >
            <FormItem>
              <FormLabel className="flex flex-row items-center gap-2">
                <Users className="w-4 h-4" />
                <h3 className="text-lg">Client information</h3>
              </FormLabel>
              <FormDescription>
                Update the client&apos;s personal details.
              </FormDescription>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input value={client?.email ?? ''} disabled />
                  </FormControl>
                </FormItem>

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <PhoneNumberInput
                          defaultCountry={defaultCountry}
                          value={field.value || ''}
                          onChange={(value) => field.onChange(value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dob"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of birth</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground',
                              )}
                            >
                              <CalendarIcon />
                              {field.value ? (
                                format(field.value, 'PPP')
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <AppCalendar
                              mode="single"
                              captionLayout="dropdown"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date > new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value ?? 'none'}
                          onValueChange={(value) =>
                            field.onChange(
                              value === 'none'
                                ? null
                                : (value as ClientGenderOption),
                            )
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="non-binary">
                              Non-binary
                            </SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormItem>

            <Separator />

            <FormField
              control={form.control}
              name="check_in_day"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row items-center gap-2">
                    <FaRegCalendar className="w-4 h-4" />{' '}
                    <h3 className="text-lg">Check-in day</h3>
                  </FormLabel>
                  <FormDescription>
                    Reminder to check in is sent at 9am on this day.
                  </FormDescription>
                  <FormControl>
                    <Select
                      value={
                        field.value !== undefined ? String(field.value) : 'none'
                      }
                      onValueChange={(value) =>
                        field.onChange(
                          value === 'none' ? undefined : Number(value),
                        )
                      }
                    >
                      <SelectTrigger className="w-[240px]">
                        <SelectValue placeholder="Select a day" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No check-in day</SelectItem>
                        <SelectItem value="0">Sunday</SelectItem>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row items-center gap-2">
                    <FaRegCalendar className="w-4 h-4" />{' '}
                    <h3 className="text-lg">Start date</h3>
                  </FormLabel>
                  <FormDescription>
                    Select this client&apos;s start date.
                  </FormDescription>

                  <FormControl>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'w-[240px] justify-start text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          <CalendarIcon />
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <AppCalendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row items-center gap-2">
                    <FaRegClock className="w-4 h-4" />{' '}
                    <h3 className="text-lg">Duration</h3>
                  </FormLabel>
                  <FormDescription>The duration in weeks.</FormDescription>

                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      className="w-[200px]"
                      placeholder="weeks"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <FormField
              control={form.control}
              name="weight_unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row items-center gap-2">
                    <Scale className="w-4 h-4" />{' '}
                    <h3 className="text-lg">Weight unit</h3>
                  </FormLabel>
                  <FormDescription>
                    Set the default weight unit for this client.
                  </FormDescription>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-[240px]">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kilograms (kg)</SelectItem>
                        <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="distance_unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row items-center gap-2">
                    <RouteIcon className="w-4 h-4" />{' '}
                    <h3 className="text-lg">Distance unit</h3>
                  </FormLabel>
                  <FormDescription>
                    Set the default distance unit for this client.
                  </FormDescription>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-[240px]">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="km">Kilometers (km)</SelectItem>
                        <SelectItem value="mi">Miles (mi)</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="client_category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client category</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="grid gap-3 sm:grid-cols-3"
                    >
                      <FormItem className="flex items-center space-x-0">
                        <FormControl>
                          <RadioGroupItem
                            value="online"
                            id="settings-client-category-online"
                            className="peer sr-only"
                          />
                        </FormControl>
                        <FormLabel
                          htmlFor="settings-client-category-online"
                          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-muted bg-background p-4 text-sm font-medium text-muted-foreground transition hover:border-primary/40 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:text-primary"
                        >
                          <Laptop className="h-5 w-5" />
                          Online
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-0">
                        <FormControl>
                          <RadioGroupItem
                            value="in-person"
                            id="settings-client-category-in-person"
                            className="peer sr-only"
                          />
                        </FormControl>
                        <FormLabel
                          htmlFor="settings-client-category-in-person"
                          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-muted bg-background p-4 text-sm font-medium text-muted-foreground transition hover:border-primary/40 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:text-primary"
                        >
                          <Users className="h-5 w-5" />
                          In-person
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-0">
                        <FormControl>
                          <RadioGroupItem
                            value="hybrid"
                            id="settings-client-category-hybrid"
                            className="peer sr-only"
                          />
                        </FormControl>
                        <FormLabel
                          htmlFor="settings-client-category-hybrid"
                          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-muted bg-background p-4 text-sm font-medium text-muted-foreground transition hover:border-primary/40 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:text-primary"
                        >
                          <Shuffle className="h-5 w-5" />
                          Hybrid
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    Choose how you train with this client.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row items-center gap-2">
                    <Globe className="w-4 h-4" />{' '}
                    <h3 className="text-lg">Timezone</h3>
                  </FormLabel>
                  <FormDescription>
                    Select the timezone for this client.
                  </FormDescription>

                  <FormControl>
                    <TimeZoneSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Features Section */}
            <FormItem ref={featuresRef}>
              <FormLabel className="flex flex-row items-center gap-2">
                <SettingsIcon className="w-4 h-4" />{' '}
                <h3 className="text-lg">Features</h3>
              </FormLabel>
              <FormDescription>
                Enable or disable features for this client.
              </FormDescription>

              {(clientFeaturesLoading || featuresLoading) && (
                <div className="flex items-center gap-2">
                  <LoadingSpinner />
                  <span className="text-sm text-muted-foreground">
                    Loading features...
                  </span>
                </div>
              )}

              {clientFeatures && clientFeatures.length > 0 ? (
                <div className="space-y-4">
                  {clientFeatures.map((clientFeature) => {
                    const feature = features?.find(
                      (f) => f.key === clientFeature.feature_key,
                    )
                    return (
                      <div
                        key={clientFeature.feature_key}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">
                              {feature?.name || clientFeature.feature_key}
                            </h4>
                            {updatingFeatureKey ===
                              clientFeature.feature_key && (
                              <LoadingSpinner className="w-4 h-4" />
                            )}
                          </div>
                          {feature?.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {feature.description}
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={isFeatureEnabled(clientFeature.feature_key)}
                          onCheckedChange={(enabled) =>
                            toggleFeature(clientFeature.feature_key, enabled)
                          }
                          disabled={
                            updatingFeatureKey === clientFeature.feature_key ||
                            clientFeaturesLoading ||
                            featuresLoading
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No features available.
                </p>
              )}
            </FormItem>

            <Separator />

            {(canArchiveClient || canUnarchiveClient) && (
              <FormItem>
                <FormLabel className="flex flex-row items-center gap-2">
                  {client?.status === 'ARCHIVED' ? (
                    <ArchiveRestore className="w-4 h-4" />
                  ) : (
                    <ArchiveX className="w-4 h-4" />
                  )}{' '}
                  <h3 className="text-lg">
                    {client?.status === 'ARCHIVED' ? 'Unarchive' : 'Archive'}
                  </h3>
                </FormLabel>
                <FormDescription>
                  {client?.status === 'ARCHIVED'
                    ? 'Restore client access to the app.'
                    : 'Archive this client, removing access to the app but still keeping records for your sake.'}
                </FormDescription>
                <Button
                  variant={
                    client?.status === 'ARCHIVED' ? 'outline' : 'destructive'
                  }
                  type="button"
                  onClick={
                    client?.status === 'ARCHIVED'
                      ? unarchiveClient
                      : () => setArchiveConfirmOpen(true)
                  }
                  className="w-32 my-0"
                  disabled={loading}
                >
                  {loading ? (
                    <LoadingSpinner />
                  ) : client?.status === 'ARCHIVED' ? (
                    'Unarchive'
                  ) : (
                    'Archive Client'
                  )}
                </Button>
              </FormItem>
            )}

            <Separator />

            <Button
              type="submit"
              className="w-32"
              disabled={loading || !isDirty}
            >
              {loading ? <LoadingSpinner /> : 'Save'}
            </Button>
          </form>
        </Form>
      </Card>
    </div>
  )
}

function inferCountryFromLocale(): Country | null {
  if (typeof navigator === 'undefined') return null
  const locale = navigator.language || ''
  const match = locale.match(/-([A-Z]{2})$/i)
  if (!match) return null
  return match[1].toUpperCase() as Country
}
